import { useEffect, useRef } from "react";

import type { ApolloQueryResult, WatchQueryFetchPolicy } from "@apollo/client";
import { NetworkStatus, useQuery } from "@apollo/client";

import { GET_NODES } from "../../../common/operations/queries";
import { DEFAULT_LIMIT_COUNT } from "../../../common/variables/common";
import { errorNotification } from "../../../common/variables/notification";
import { useSelectedLocationDropdown } from "../../../common/variables/selectedLocationDropdown";
import type { GetNodesQuery, GetNodesQueryVariables } from "./../../../API";

interface GetNodesDataInterface {
  data?: GetNodesQuery;
  loading: boolean;
  refetch: () => Promise<ApolloQueryResult<GetNodesQuery>>;
}

export const NODES_TABLE_LIMIT = 25;

interface UseGetNodesParams {
  locationId?: string;
  customerId?: string;
  tagId?: string;
  includeDevices?: boolean;
  fetchPolicy?: WatchQueryFetchPolicy;
}

export const useGetNodes = (
  params: UseGetNodesParams = {}
): GetNodesDataInterface => {
  const locationVariable = useSelectedLocationDropdown();
  // When customerId is explicitly provided, don't fall back to the global location
  // dropdown — the caller wants all nodes for that customer, not just one location.
  const locationId =
    params.locationId ??
    (params.customerId ? undefined : locationVariable?.value);
  const customerId = params.customerId;
  const tagId = params.tagId;
  const includeDevices = params.includeDevices ?? true;
  const hasQueryInput = Boolean(locationId || customerId || tagId);

  const { data, fetchMore, loading, networkStatus, refetch } = useQuery<
    GetNodesQuery,
    GetNodesQueryVariables
  >(GET_NODES, {
    variables: {
      limit: DEFAULT_LIMIT_COUNT,
      locationId,
      customerId,
      tagId,
      includeDevices,
    },
    skip: !hasQueryInput,
    notifyOnNetworkStatusChange: true,
    fetchPolicy: params.fetchPolicy ?? "cache-and-network",
    nextFetchPolicy: "cache-first",
  });

  const fetchAllRunIdRef = useRef(0);
  const isFetchingAllPagesRef = useRef(false);

  useEffect(() => {
    if (!hasQueryInput) return;

    if (loading) return;

    if (networkStatus !== NetworkStatus.ready) return;

    const initialNextToken = data?.getNodes?.nextToken;

    if (!initialNextToken) return;

    if (isFetchingAllPagesRef.current) return;

    const runId = ++fetchAllRunIdRef.current;

    let cancelled = false;

    isFetchingAllPagesRef.current = true;

    const fetchAllPages = async () => {
      let currentNextToken: string | null | undefined = initialNextToken;
      const seenTokens = new Set<string>();

      try {
        while (
          !cancelled &&
          runId === fetchAllRunIdRef.current &&
          currentNextToken
        ) {
          if (seenTokens.has(currentNextToken)) break;

          seenTokens.add(currentNextToken);

          const response: ApolloQueryResult<GetNodesQuery> = await fetchMore({
            variables: {
              limit: DEFAULT_LIMIT_COUNT,
              locationId,
              customerId,
              tagId,
              includeDevices,
              nextToken: currentNextToken,
            },
          });

          currentNextToken = response.data?.getNodes?.nextToken ?? null;
        }
      } catch (error) {
        console.error("Error fetching additional node pages:", error);

        errorNotification();
      } finally {
        if (runId === fetchAllRunIdRef.current) {
          isFetchingAllPagesRef.current = false;
        }
      }
    };

    void fetchAllPages();

    return () => {
      cancelled = true;

      if (runId === fetchAllRunIdRef.current) {
        isFetchingAllPagesRef.current = false;
      }
    };
  }, [
    data?.getNodes?.nextToken,
    fetchMore,
    loading,
    locationId,
    customerId,
    tagId,
    includeDevices,
    networkStatus,
    hasQueryInput,
  ]);

  const nodesLoading =
    loading ||
    networkStatus === NetworkStatus.fetchMore ||
    networkStatus === NetworkStatus.refetch;

  return { data, loading: nodesLoading, refetch };
};
